import React, { useEffect, useState, useRef } from "react";
import { Icon, jnjArrowLeft, jnjArrowRight } from "@jmc/solid-design-system/src/components/atoms/Icon/Icon";
import { BreakPoint, useMediaQuery } from "@jmc/solid-design-system/src/hooks/useMediaQuery";
import classnames from "classnames";
import { useJnjBranding } from "@jmc/utils/hooks/useJnjBranding";
import { useLocale } from "@jmc/core/src/hooks/useLocale/index";
import { getLocaleRtl } from "@jmc/solid-design-system/src/utils/languages";
import { mdiArrowLeft, mdiArrowRight } from "@mdi/js";

import style from "./style.module.scss";
import debounce from "@jmc/utils/utils/debounce";
import { useTranslation } from "react-i18next";

interface PropTypes {
    id?: string;
    children: React.ReactNode[];
    backgroundColor?: string;
    fullWidth?: boolean;
}

export enum NumberOfCards {
    DESKTOP = 3,
    TABLET = 2,
    MOBILE = 1,
}

const SCROLL_THROTTLE = 50;

/**
 * Slider
 */
export const Slider = (props: PropTypes): JSX.Element => {
    const { id = "Slider.Element", children, backgroundColor = null, fullWidth = false } = props;

    const locale = useLocale();
    const rtl = getLocaleRtl(locale);
    const [hideLeft, setHideLeft] = useState(rtl ? false : true);
    const [hideRight, setHideRight] = useState(rtl ? true : false);
    const [currentScrollBarIndex, setCurrentScrollBarIndex] = useState(0);
    const [sliderScrollIndex, setSliderScrollIndex] = useState(0);
    const sliderContainerRef = useRef(null);
    const sliderSectionRef = useRef(null);
    const scrollBarRef = useRef(null);
    const isMobile = useMediaQuery(BreakPoint.md);
    const isTablet = useMediaQuery(BreakPoint.lg);
    const numberOfCards = children?.length;
    const outerWidth = fullWidth ? 40 : 60;
    const { t } = useTranslation();
    const { jnjFullBranded } = useJnjBranding();

    const getCardNumber = () => {
        if (isMobile) {
            return NumberOfCards.MOBILE;
        } else if (isTablet) {
            return NumberOfCards.TABLET;
        } else {
            return NumberOfCards.DESKTOP;
        }
    };

    // container element section
    const sectionContainer = sliderContainerRef?.current;
    const cardContainer = sliderSectionRef?.current?.firstChild;

    const getCardWidth = (): number => {
        const cardWidth = cardContainer?.getBoundingClientRect()?.width;
        const cardMargin =
            (typeof window !== "undefined" &&
                cardContainer &&
                parseFloat(window?.getComputedStyle(cardContainer)?.marginInlineEnd)) ||
            0;
        const width = cardWidth + cardMargin;
        return width;
    };

    // on slider scroll update scrollbar index
    const onSliderScroll = (): void => {
        const currentScrollWidth = Math.round(sectionContainer?.scrollLeft / getCardWidth());
        if (currentScrollWidth > sliderScrollIndex || currentScrollWidth < sliderScrollIndex) {
            setSliderScrollIndex(currentScrollWidth);
            setCurrentScrollBarIndex(currentScrollWidth);
        }
    };

    // show/hide icons
    const checkScroll = (): void => {
        if (
            rtl
                ? sectionContainer?.offsetWidth + outerWidth - sectionContainer?.scrollLeft >=
                  sectionContainer?.scrollWidth - 1
                : sectionContainer?.scrollLeft <= outerWidth
        ) {
            setHideLeft(true);
        } else {
            setHideLeft(false);
        }

        if (
            rtl
                ? sectionContainer?.scrollLeft >= -1 * outerWidth
                : sectionContainer?.scrollLeft + sectionContainer?.offsetWidth + outerWidth >=
                  sectionContainer?.scrollWidth - 1
        ) {
            setHideRight(true);
        } else {
            setHideRight(false);
        }
        onSliderScroll();
    };

    const debouncedCheckScroll = debounce(checkScroll, SCROLL_THROTTLE);

    useEffect(() => {
        checkScroll();
        sectionContainer?.addEventListener("scroll", debouncedCheckScroll, true);

        return (): void => {
            sectionContainer?.removeEventListener("scroll", debouncedCheckScroll, true);
        };
    });

    // set slider thumb width
    useEffect(() => {
        const scrollBarContainer = scrollBarRef?.current;
        if (scrollBarContainer && numberOfCards) {
            const thumbWidth = `${
                100 /
                (isMobile
                    ? numberOfCards
                    : numberOfCards - ((jnjFullBranded ? getCardNumber() : NumberOfCards.DESKTOP) - 1))
            }%`;
            scrollBarContainer?.style?.setProperty("--scrollbar-thumb-width", thumbWidth);
        }
    });

    // left arrow click
    const slideLeft = (): void => {
        if (sectionContainer && jnjFullBranded)
            rtl
                ? (sectionContainer.scrollLeft =
                      Math.max(currentScrollBarIndex - 1, -1 * (numberOfCards - getCardNumber())) * getCardWidth())
                : (sectionContainer.scrollLeft = Math.max(currentScrollBarIndex - 1, 0) * getCardWidth());
        else if (sectionContainer)
            sectionContainer.scrollLeft = sectionContainer.scrollLeft - NumberOfCards.DESKTOP * getCardWidth();
    };

    // right arrow click
    const slideRight = (): void => {
        if (sectionContainer && jnjFullBranded)
            sectionContainer.scrollLeft = rtl
                ? Math.min(currentScrollBarIndex + 1, 0) * getCardWidth()
                : Math.min(currentScrollBarIndex + 1, numberOfCards - getCardNumber()) * getCardWidth();
        else if (sectionContainer)
            sectionContainer.scrollLeft = sectionContainer.scrollLeft + NumberOfCards.DESKTOP * getCardWidth();
    };

    // left button Mouse Leave peep
    const lefttButtonMouseLeavePeep = (): void => {
        if (sectionContainer?.scrollLeft > 0) sectionContainer.scrollLeft = sectionContainer.scrollLeft + outerWidth;
    };

    // left button Mouse Enter peep
    const lefttButtonMouseEnterPeep = (): void => {
        if (sectionContainer?.scrollLeft > 0) sectionContainer.scrollLeft = sectionContainer.scrollLeft - outerWidth;
    };

    // right button Mouse Leave peep
    const rightButtonMouseLeavePeep = (): void => {
        if (sectionContainer?.scrollLeft + sectionContainer?.offsetWidth <= sectionContainer?.scrollWidth - 1)
            sectionContainer.scrollLeft = sectionContainer.scrollLeft - outerWidth;
    };

    // right button Mouse Enter peep
    const rightButtonMouseEnterPeep = (): void => {
        if (sectionContainer?.scrollLeft + sectionContainer?.offsetWidth <= sectionContainer?.scrollWidth - 1)
            sectionContainer.scrollLeft = sectionContainer.scrollLeft + outerWidth;
    };

    // on scrollbar slide
    const handleScrollBarChange = (scrollBarIndex: number): void => {
        if (sectionContainer) sectionContainer.scrollLeft = scrollBarIndex * getCardWidth();
    };

    const cardWidth = getCardWidth();
    const overFlowWidth = cardWidth > 0 ? cardWidth : outerWidth;

    return (
        <div
            className={classnames(style.element, fullWidth ? null : style.containerWidth)}
            data-test-id={id}
            style={{ "--overflow-width": `${fullWidth ? overFlowWidth : outerWidth}px` } as React.CSSProperties}
        >
            <div className={style.wrapper} data-test-id="Slider.Wrapper">
                {!jnjFullBranded && (
                    <button
                        className={classnames(
                            style.arrowButtonLeft,
                            hideLeft ? style.disabled : null,
                            backgroundColor ? classnames(style.arrowButtonColor, style[backgroundColor]) : null,
                            fullWidth ? null : style.arrowSpacing,
                        )}
                        onClick={slideLeft}
                        onMouseEnter={lefttButtonMouseEnterPeep}
                        onMouseLeave={lefttButtonMouseLeavePeep}
                        data-test-id="Slider.LeftArrow"
                        title={t("Previous Slides", { ns: "common" })}
                    >
                        <Icon
                            className={backgroundColor ? style.arrowColor : null}
                            icon={mdiArrowLeft}
                            verticalAlign="middle"
                            size="medium"
                        />
                    </button>
                )}
                <div className={style.sliderContainer} ref={sliderContainerRef} data-test-id="Slider.Container">
                    <div className={style.sliderSection} ref={sliderSectionRef}>
                        {React.Children.map(children, (child, index: number) => {
                            const isInActive =
                                index < currentScrollBarIndex ||
                                index > currentScrollBarIndex + NumberOfCards.DESKTOP - 1;
                            return (
                                <div key={index} className={isInActive ? style.inActive : null}>
                                    {child}
                                </div>
                            );
                        })}
                        {!jnjFullBranded && <div className={style.sectionEnd}></div>}
                    </div>
                </div>
                {!jnjFullBranded && (
                    <button
                        className={classnames(
                            style.arrowButtonRight,
                            hideRight ? style.disabled : null,
                            backgroundColor ? classnames(style.arrowButtonColor, style[backgroundColor]) : null,
                            fullWidth ? null : style.arrowSpacing,
                        )}
                        onClick={slideRight}
                        onMouseEnter={rightButtonMouseEnterPeep}
                        onMouseLeave={rightButtonMouseLeavePeep}
                        data-test-id="Slider.RightArrow"
                        title={t("Next Slides", { ns: "common" })}
                    >
                        <Icon
                            className={backgroundColor ? style.arrowColor : null}
                            icon={mdiArrowRight}
                            verticalAlign="middle"
                            size="medium"
                        />
                    </button>
                )}
            </div>
            <div
                className={classnames(style.scrollbarContainer, isMobile ? style.showScrollBar : null)}
                data-test-id="Slider.ScrollBar"
            >
                <input
                    type="range"
                    min="0"
                    max={
                        numberOfCards -
                            (jnjFullBranded
                                ? getCardNumber()
                                : isMobile
                                  ? NumberOfCards.MOBILE
                                  : NumberOfCards.DESKTOP) || 0
                    }
                    value={rtl ? 0 - currentScrollBarIndex : currentScrollBarIndex}
                    ref={scrollBarRef}
                    className={classnames(style.rangeSlider, backgroundColor ? style.scrollBarColor : null)}
                    onChange={(e) => {
                        const currentValue = parseInt(e?.target?.value);
                        setCurrentScrollBarIndex(rtl ? 0 - currentValue : currentValue);
                        handleScrollBarChange(rtl ? 0 - currentValue : currentValue);
                    }}
                />
                {jnjFullBranded && (
                    <div className={style.buttonContainer}>
                        <button
                            className={classnames(
                                style.arrowButtonLeft,
                                hideLeft ? style.disabled : null,
                                fullWidth ? null : style.arrowSpacing,
                            )}
                            onClick={slideLeft}
                            data-test-id="Slider.LeftArrow"
                            title={rtl ? t("Next Slides", { ns: "common" }) : t("Previous Slides", { ns: "common" })}
                        >
                            <Icon
                                icon={jnjArrowLeft}
                                size="xs"
                                type="jnj"
                                color={hideLeft ? "text-gray-500" : "white"}
                            />
                        </button>
                        <button
                            className={classnames(
                                style.arrowButtonRight,
                                hideRight ? style.disabled : null,
                                fullWidth ? null : style.arrowSpacing,
                            )}
                            onClick={slideRight}
                            data-test-id="Slider.RightArrow"
                            title={rtl ? t("Previous Slides", { ns: "common" }) : t("Next Slides", { ns: "common" })}
                        >
                            <Icon
                                icon={jnjArrowRight}
                                size="xs"
                                type="jnj"
                                color={hideRight ? "text-gray-500" : "white"}
                            />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
